/**
 * Save-confirm modal + post-save success/error toast.
 *
 * Pattern designed for librarian users (e.g. Jo) who aren't deeply
 * computer-literate: a pre-save modal lists the changed fields by
 * name so the librarian can review what's about to commit, and a
 * top-right toast confirms the save landed (or surfaces an error)
 * without requiring a glance at the 12px gray statusEl in the
 * bottom-right corner.
 *
 * Filed against OMON-068 follow-up after Ed asked for both
 * patterns C (diff modal) and A (toast) on 2026-05-10.
 */

import type { EditingDetail } from "../state";
import { modalContainer } from "../dom";
import { escapeHtml } from "../utils/html";
import { LEVEL_NAMES } from "../main";
import { stripGuidanceHeader } from "./guidanceParser";

export interface OriginalSnapshot {
  title: string;
  smartGoal: string;
  /** OMON-174: canonical monitored-event prompt. */
  monitoredEventPrompt: string;
  /** Zammad #49088: monitored-event carer guidance. */
  monitoredEventGuidance: string;
  /** Ed 2026-09-29: a goal's carer guidance as one text. */
  guidanceText: string;
  attainment: string[];
  prompts: string[];
  categoryCode: string;
  subcategoryCode: string;
  capabilities: string[];
  recordingKind: string;
  status: string;
  // OMON-172: librarian per-fact policy flags.
  targetable: boolean;
  escalatable: boolean;
  escalationThreshold: number | null;
  mandatory: boolean;
  // ADR-0035 am.8.
  shiftRolesDefault: string[];
  rolesLocked: boolean;
}

export interface DiffSummary {
  hasChanges: boolean;
  changedFields: string[];
}

/**
 * Compute a list of human-readable field names that differ between
 * the in-flight edit state and the original goal. Used to populate
 * the confirm modal so the librarian sees exactly what's about to
 * be saved without seeing the underlying field IDs.
 */
export function diffEditingDetail(
  edited: EditingDetail,
  original: OriginalSnapshot,
): DiffSummary {
  const changes: string[] = [];
  if (edited.title !== original.title) changes.push("Title");
  if (edited.smartGoal !== original.smartGoal) changes.push("What does good look like");
  // OMON-174: canonical monitored-event prompt.
  if (edited.monitoredEventPrompt !== original.monitoredEventPrompt) changes.push("Monitored Event Prompt");
  // Zammad #49088: monitored-event carer guidance (one text, not per level).
  if (edited.monitoredEventGuidance !== original.monitoredEventGuidance) changes.push("Carer Guidance Prompts");
  if (edited.guidanceText !== original.guidanceText) changes.push("Carer guidance");
  if (edited.categoryCode !== original.categoryCode) changes.push("Goal Category");
  if (edited.subcategoryCode !== original.subcategoryCode) changes.push("Goal Sub-category");
  if ([...edited.capabilities].sort().join() !== [...original.capabilities].sort().join()) changes.push("Capabilities (D/M/S)");
  if (edited.recordingKind !== original.recordingKind) changes.push("Recording method");
  if (edited.status !== original.status) changes.push("Status (Active/Retired)");
  // OMON-172: librarian per-fact policy flags.
  if (edited.targetable !== original.targetable) changes.push("Targetable");
  if (edited.escalatable !== original.escalatable) changes.push("Escalatable");
  if (edited.escalationThreshold !== original.escalationThreshold) changes.push("Escalation threshold");
  if (edited.shiftRolesDefault.join() !== original.shiftRolesDefault.join()) changes.push("Owed by");
  if (edited.rolesLocked !== original.rolesLocked) changes.push("Owed by lock");
  for (let i = 0; i < 5; i++) {
    if (edited.attainment[i] !== original.attainment[i]) {
      changes.push(`Attainment Level ${i + 1} (${LEVEL_NAMES[i]})`);
    }
    // OMON-242 phantom-change fix (2026-07-07): compare the AUTHORED content
    // (header-stripped tail), not the raw stored string. The `# title / subtitle`
    // header is derived — `wrapPromptWithHeader` regenerates it from the current
    // title + subcategory on every capture — so it is never independent data.
    // `getObjectiveDetail` seeds default-backed prompts header-LESS while the
    // captured buffer is always header-FULL; a raw compare therefore reports a
    // phantom "Carer Guidance changed" on a pure tab-switch (fires the undo toast
    // when nothing was edited — e.g. "Fatigue / energy & pacing", whose AD doc
    // doesn't exist so every prompt is a code default). Stripping both sides makes
    // the diff header-insensitive: a genuine body edit is still caught, and a
    // title-only change is reported by the `Title` field alone.
    if (stripGuidanceHeader(edited.prompts[i] || "").trim()
        !== stripGuidanceHeader(original.prompts[i] || "").trim()) {
      changes.push(`Carer Guidance Level ${i + 1} (${LEVEL_NAMES[i]})`);
    }
  }
  return { hasChanges: changes.length > 0, changedFields: changes };
}

/**
 * Show the pre-save confirm modal with the list of changed fields.
 * Calls onConfirm if the librarian clicks Save Changes; calls
 * onCancel for clicking Cancel, the overlay, or pressing Esc.
 */
export function showSaveConfirmModal(
  diff: DiffSummary,
  onConfirm: () => void,
  onCancel: () => void,
) {
  const itemsHtml = diff.changedFields
    .map((f) => `<li>${escapeHtml(f)}</li>`)
    .join("");
  const fieldCount = diff.changedFields.length;
  const fieldWord = fieldCount === 1 ? "field" : "fields";

  modalContainer.innerHTML = `
    <div class="modal-overlay">
      <div class="modal-box">
        <h3>Confirm changes</h3>
        <p>You're about to save changes to ${fieldCount} ${fieldWord}:</p>
        <ul class="modal-diff-list">${itemsHtml}</ul>
        <p class="modal-diff-note">
          These changes will be visible to all carers immediately.
        </p>
        <div class="modal-actions">
          <button class="btn-outline" id="modalCancelSave">Cancel</button>
          <button class="btn-primary" id="modalConfirmSave">Save Changes</button>
        </div>
      </div>
    </div>
  `;

  const cleanup = () => {
    modalContainer.innerHTML = "";
    document.removeEventListener("keydown", handleEsc);
  };
  const handleEsc = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      cleanup();
      onCancel();
    }
  };
  document.addEventListener("keydown", handleEsc);

  modalContainer.querySelector(".modal-overlay")?.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).classList.contains("modal-overlay")) {
      cleanup();
      onCancel();
    }
  });
  modalContainer.querySelector("#modalCancelSave")?.addEventListener("click", () => {
    cleanup();
    onCancel();
  });
  modalContainer.querySelector("#modalConfirmSave")?.addEventListener("click", () => {
    cleanup();
    onConfirm();
  });
}

/**
 * Show a transient toast at the top-right of the page. Auto-fades
 * after 3s for success, 5s for error (errors deserve a longer dwell
 * for the librarian to read the message).
 */
/**
 * OMON-182 (2026-06-11, Ed): undo affordance on silent saves — the safety
 * boundary that replaces the retired Manage-Hierarchy mode toggle. Always-edit
 * + auto-save means a stray drag or keystroke mutates the canonical catalogue
 * instantly; the toast gives a 7s window to take it back. One at a time: a
 * newer toast replaces the previous one (its undo window has passed).
 */
let activeUndoToast: HTMLElement | null = null;
export function showUndoToast(message: string, onUndo: () => void) {
  activeUndoToast?.remove();
  const toast = document.createElement("div");
  toast.className = "app-toast app-toast-success";
  toast.style.display = "flex";
  toast.style.alignItems = "center";
  toast.style.gap = "14px";
  const text = document.createElement("span");
  text.textContent = message;
  const btn = document.createElement("button");
  btn.textContent = "Undo";
  btn.style.cssText = "background:none;border:none;color:#7c3aed;font-weight:650;font-size:13px;cursor:pointer;padding:2px 4px;";
  btn.addEventListener("click", () => {
    toast.remove();
    if (activeUndoToast === toast) activeUndoToast = null;
    onUndo();
  });
  toast.appendChild(text);
  toast.appendChild(btn);
  document.body.appendChild(toast);
  activeUndoToast = toast;
  requestAnimationFrame(() => toast.classList.add("visible"));
  setTimeout(() => {
    toast.classList.remove("visible");
    setTimeout(() => {
      toast.remove();
      if (activeUndoToast === toast) activeUndoToast = null;
    }, 300);
  }, 30000);
}

export function showToast(message: string, type: "success" | "error" = "success") {
  const toast = document.createElement("div");
  toast.className = `app-toast app-toast-${type}`;
  toast.textContent = message;
  document.body.appendChild(toast);
  // Trigger CSS transition on next frame so the entry animates.
  requestAnimationFrame(() => toast.classList.add("visible"));
  const dwell = type === "error" ? 5000 : 3000;
  setTimeout(() => {
    toast.classList.remove("visible");
    setTimeout(() => toast.remove(), 300);
  }, dwell);
}
